import React, { useEffect, useRef, useState } from "react";
import {
  collectionGroup,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import { Download, Clock, Calendar, ChevronRight, Loader2 } from "lucide-react";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import moment from "moment";
import { db } from "../../firebase";
import { useParams } from "react-router-dom";

const Bookings = () => {
  const { bookingId } = useParams();
  const qrRef = useRef(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookeeProfile, setBookeeProfile] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) return;
      try {
        const bookingsQuery = collectionGroup(db, "bookings");
        const querySnapshot = await getDocs(bookingsQuery);
        const foundDoc = querySnapshot.docs.find((doc) => doc.id === bookingId);

        if (foundDoc) {
          const bookingData = { id: foundDoc.id, ...foundDoc.data() };
          setBooking(bookingData);

          if (bookingData.bookee) {
            fetchBookeeProfile(bookingData.bookee);
          }
        } else {
          setBooking(null);
        }
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchBookeeProfile = async (bookeeId) => {
      try {
        const bookeeRef = doc(db, "members", bookeeId);
        const bookeeSnap = await getDoc(bookeeRef);

        if (bookeeSnap.exists()) {
          setBookeeProfile({ id: bookeeSnap.id, ...bookeeSnap.data() });
        } else {
          setBookeeProfile(null);
        }
      } catch (error) {
        console.error("Error fetching bookee profile:", error);
      }
    };

    fetchBooking();
  }, [bookingId]);

  const captureQR = async () => {
    if (!qrRef.current) return;

    try {
      const canvas = await html2canvas(qrRef.current, {
        useCORS: true,
        logging: true,
      });
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `booking-${booking.id}.png`;
      link.click();
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-white bg-opacity-50">
        <Loader2 className="animate-spin mr-2 text-gray-600" size={24} />
        <p className="text-lg text-gray-600">Please Wait...</p>
      </div>
    );
  }

  if (!booking) return <p>No booking found.</p>;

  const formattedDate = booking?.assignedData
    ? moment(booking?.assignedData?.date?.seconds * 1000).format("MMMM D, YYYY")
    : "No Date";
  const formattedTime = booking?.assignedData
    ? moment(booking?.assignedData?.time?.seconds * 1000).format("h:mm A")
    : "";

  return (
    <div
      ref={qrRef}
      className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-xl mt-10"
    >
      <h2 className="text-xl font-bold text-gray-900">Booking Details</h2>
      <div className="flex flex-col items-center gap-4 mt-4">
        <div>
          <img
            src={
              bookeeProfile?.profile_image ||
              "https://img.freepik.com/free-psd/contact-icon-illustration-isolated_23-2151903337.jpg"
            }
            alt="Avatar"
            className="h-36 w-36 rounded-full object-cover bg-gray-400"
          />
          <p className="mt-2 text-center">
            {bookeeProfile?.first_name} {bookeeProfile?.last_name}
          </p>
        </div>
        <QRCode
          id={`qr-${booking.id}`}
          value={`https://www.booking.ooobcs.org/bookings/${booking.id}`}
          size={150}
        />
        <button
          onClick={captureQR}
          className="flex bg-black items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-full shadow hover:bg-gray-700 transition"
        >
          <Download size={16} /> Download QR
        </button>
      </div>

      <div className="mt-4 space-y-3 text-sm">
        <p>
          <strong>Purpose:</strong> {booking?.purpose || booking?.customPurpose}
        </p>
        <p>
          <strong>Visit Type:</strong> {booking?.visitType}
        </p>
        <p>
          <strong>Explanation:</strong> {booking?.explanation}
        </p>
        <p>
          <strong>Status:</strong>
          <span
            className={`px-2 text-sm py-1 rounded-full ${
              booking?.status === "pending"
                ? "bg-[#F79009] text-white"
                : booking?.status === "Processing"
                ? "bg-purple-300"
                : booking?.status === "rejected"
                ? "bg-red-300"
                : "bg-green-500 text-white"
            }`}
          >
            {booking.status}
          </span>
        </p>
      </div>

      <div className="border-t my-4"></div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200">
            <Clock className="text-gray-600" size={18} />
          </div>
          <span className="text-sm text-gray-600">
            {formattedTime || "Not assigned"}
          </span>
        </div>
        <div className="h-px w-12 bg-gray-400"></div>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200">
            <Calendar className="text-gray-600" size={18} />
          </div>
          <span className="text-sm text-gray-600">{formattedDate}</span>
        </div>
        <ChevronRight className="text-gray-600" size={18} />
      </div>
      <div className="mt-4 text-center text-sm text-gray-600">
        {booking?.assignedData ? (
          <p>
            <strong>Scheduled:</strong> {formattedTime} - {formattedDate}
          </p>
        ) : (
          <p className="text-gray-500">Visit not assigned yet</p>
        )}
      </div>
    </div>
  );
};

export default Bookings;
